import type {
  AffiliateLinkResource,
  AffiliateLinkResourceV2,
  ImageResource,
  StoreResource,
} from '@metapic-apps/shared'

import type { AdvertiserType } from '@/lib/types/advertiser.types'

export type AffiliateLink = {
  id: number
  title?: string
  advertiserName: string
  advertiserLogoUrl?: string
  image?: ImageResource
  approvedEarnings?: { amount: string; currency: string }
  potentialEarnings?: { amount: string; currency: string }
  createdAt: string
  clicks: number
  mtpcUrl: string
}

const normalizeAffiliateLink = (raw: AffiliateLinkResourceV2): AffiliateLink => ({
  id: raw.id,
  title: raw.title,
  advertiserName: raw.advertiser?.name ?? '',
  advertiserLogoUrl: raw.advertiser?.logo?.url,
  image: raw.image,
  approvedEarnings: raw.stats?.cost_creator_approved,
  potentialEarnings: raw.stats?.cost_creator_pending,
  createdAt: raw.created_at,
  clicks: raw.stats?.number_of_clicks ?? 0,
  mtpcUrl: raw.mtpc_url,
})

export const normalizeAffiliateLinks = (raws: AffiliateLinkResourceV2[]): AffiliateLink[] =>
  raws.map(normalizeAffiliateLink)

export type AffiliateLinkDetail = {
  id: number
  advertiserName?: string
  advertiserLogoUrl?: string
  image?: ImageResource
  createdAt?: string
  mtpcUrl: string
  title?: string
  targetUrl?: string
  originalUrl?: string
  affiliateUrl?: string
}

export const normalizeAffiliateLinkDetail = (raw: AffiliateLinkResource): AffiliateLinkDetail => ({
  id: raw.id ?? 0,
  advertiserName: raw.advertiser?.name ?? '',
  advertiserLogoUrl: raw.advertiser?.logo?.url,
  image: raw.image,
  createdAt: raw.created_at,
  mtpcUrl: raw.mtpc_url ?? '',
  title: raw.title,
  targetUrl: raw.url,
  originalUrl: raw.original_url,
  affiliateUrl: raw.affiliate_url,
})

export const normalizeMatchingAdvertisers = (advertisers: StoreResource[] = []): AdvertiserType[] =>
  advertisers.map((advertiser) => ({
    id: advertiser.id,
    name: advertiser.name,
    currency: advertiser.currency ?? '',
    logo: {
      key: advertiser.logo?.key,
      baseUrl: advertiser.logo?.base_url,
      url: advertiser.logo?.url ?? advertiser.logo_url,
    },
  }))

const affiliateLinkIncludes = ['stats', 'advertiser'] as const

export const makeQueryParamsForAffiliateLinks = (userId: number, page: number): URLSearchParams => {
  const params = new URLSearchParams({ user_id: String(userId), page: String(page), size: '20' })
  affiliateLinkIncludes.forEach((include) => {
    params.append('include[]', include)
  })
  return params
}
